import React, { useState, useContext } from 'react';
import styled from 'styled-components';
import { AuthContext } from '../../context/AuthContext';

const LoginContainer = styled.div`
  display: flex;
  min-height: 100vh;
  background: linear-gradient(135deg, #141340 0%, #1a1a4a 50%, #2d2d6b 100%);
  align-items: center;
  justify-content: center;
  padding: 2rem;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('/pfrs.jpeg') center/cover;
    opacity: 0.03;
    z-index: 1;
  }

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const LoginCard = styled.div`
  display: flex;
  max-width: 1200px;
  width: 100%;
  background: #ffffff;
  border-radius: 24px;
  box-shadow: 0 30px 60px rgba(0, 0, 0, 0.2);
  overflow: hidden;
  position: relative;
  z-index: 2;
  backdrop-filter: blur(10px);

  @media (max-width: 768px) {
    flex-direction: column;
    max-width: 400px;
  }
`;

const LeftSection = styled.div`
  flex: 1;
  background: linear-gradient(135deg, #141340 0%, #1e1e50 50%, #2d2d6b 100%);
  padding: 3rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: relative;
  text-align: center;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 30% 30%, rgba(246, 139, 33, 0.1) 0%, transparent 50%);
    pointer-events: none;
  }

  @media (max-width: 768px) {
    padding: 2rem;
    min-height: 250px;
  }
`;

const RightSection = styled.div`
  flex: 1;
  padding: 3rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  background: #ffffff;

  @media (max-width: 768px) {
    padding: 2rem;
  }
`;

const BrandContainer = styled.div`
  position: relative;
  z-index: 2;
`;

const LogoContainer = styled.div`
  margin-bottom: 2rem;
  position: relative;
`;

const LogoImage = styled.img`
  width: 100px;
  height: 100px;
  border-radius: 50%;
  border: 4px solid #F68B21;
  box-shadow: 0 15px 35px rgba(246, 139, 33, 0.4);
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.05) rotate(5deg);
  }

  @media (max-width: 768px) {
    width: 80px;
    height: 80px;
  }
`;

const SystemName = styled.h1`
  color: #ffffff;
  font-size: 2.2rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 1.8rem;
  }
`;

const SystemAcronym = styled.h2`
  color: #F68B21;
  font-size: 1.3rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
  letter-spacing: 3px;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.2);

  @media (max-width: 768px) {
    font-size: 1.1rem;
    letter-spacing: 2px;
  }
`;

const SystemDescription = styled.p`
  color: rgba(255, 255, 255, 0.9);
  font-size: 1rem;
  line-height: 1.6;
  margin-bottom: 1.5rem;
  max-width: 300px;

  @media (max-width: 768px) {
    font-size: 0.9rem;
    margin-bottom: 1rem;
  }
`;

const FeatureList = styled.ul`
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.9rem;
  line-height: 1.8;
  list-style: none;
  padding: 0;
  margin: 0;
  display: none;

  @media (min-width: 769px) {
    display: block;
  }
`;

const FeatureItem = styled.li`
  margin-bottom: 0.5rem;
  position: relative;
  padding-left: 1.5rem;

  &::before {
    content: '✓';
    position: absolute;
    left: 0;
    color: #F68B21;
    font-weight: bold;
  }
`;

const FormContainer = styled.div`
  width: 100%;
  max-width: 400px;
  margin: 0 auto;
`;

const FormTitle = styled.h2`
  color: #141340;
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  text-align: center;

  @media (max-width: 768px) {
    font-size: 1.8rem;
  }
`;

const FormSubtitle = styled.p`
  color: #666;
  font-size: 1rem;
  text-align: center;
  margin-bottom: 2.5rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const StyledLabel = styled.label`
  color: #141340;
  font-weight: 600;
  font-size: 0.9rem;
`;

const StyledInput = styled.input`
  padding: 1.2rem;
  border: 2px solid #e0e0e0;
  border-radius: 12px;
  font-size: 1rem;
  transition: all 0.3s ease;
  background: #ffffff;

  &:focus {
    outline: none;
    border-color: #F68B21;
    box-shadow: 0 0 0 4px rgba(246, 139, 33, 0.1);
    transform: translateY(-2px);
  }

  &::placeholder {
    color: #999;
  }
`;

const LoginButton = styled.button`
  background: linear-gradient(135deg, #F68B21 0%, #e67c1a 100%);
  color: white;
  padding: 1.2rem 2rem;
  border: none;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 1rem;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s;
  }

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 15px 30px rgba(246, 139, 33, 0.4);
    
    &::before {
      left: 100%;
    }
  }

  &:active {
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    
    &:hover {
      transform: none;
      box-shadow: none;
    }
  }
`;

const ErrorMessage = styled.div`
  color: #dc3545;
  font-size: 0.9rem;
  text-align: center;
  margin-top: 1rem;
  padding: 1rem;
  background: #f8d7da;
  border-radius: 10px;
  border: 1px solid #f5c6cb;
  animation: shake 0.5s ease-in-out;

  @keyframes shake {
    0%, 100% { transform: translateX(0); }
    25% { transform: translateX(-5px); }
    75% { transform: translateX(5px); }
  }
`;

const DeveloperCredit = styled.div`
  position: absolute;
  bottom: 1rem;
  right: 1rem;
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.8rem;
  z-index: 3;

  @media (max-width: 768px) {
    position: static;
    text-align: center;
    margin-top: 2rem;
    color: #666;
  }
`;

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [logoError, setLogoError] = useState(false);
  
  const { login, error, isLoading } = useContext(AuthContext);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (email && password) {
      await login(email, password);
    }
  };

  const handleLogoError = () => {
    setLogoError(true);
  };

  return (
    <LoginContainer>
      <LoginCard>
        <LeftSection>
          <BrandContainer>
            <LogoContainer>
              {!logoError ? (
                <LogoImage 
                  src="/pfrs.jpeg" 
                  alt="PFRS Logo" 
                  onError={handleLogoError}
                />
              ) : (
                <div style={{
                  width: '100px',
                  height: '100px',
                  borderRadius: '50%',
                  border: '4px solid #F68B21',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.8rem',
                  color: '#F68B21',
                  backgroundColor: 'rgba(246, 139, 33, 0.1)',
                  fontWeight: 'bold'
                }}>
                  PFRS
                </div>
              )}
            </LogoContainer>
            
            <SystemName>Project Fund<br />Request System</SystemName>
            <SystemAcronym>PFRS</SystemAcronym>
            
            <SystemDescription>
              Seamless financial control, streamlined workflows, 
              and intelligent project management.
            </SystemDescription>
            
            <FeatureList>
              <FeatureItem>Real-time project tracking</FeatureItem>
              <FeatureItem>Automated approval workflows</FeatureItem>
              <FeatureItem>Comprehensive financial reporting</FeatureItem>
              <FeatureItem>Multi-role access control</FeatureItem>
              <FeatureItem>Mobile-responsive interface</FeatureItem>
            </FeatureList>
          </BrandContainer>
        </LeftSection>

        <RightSection>
          <FormContainer>
            <FormTitle>Welcome Back</FormTitle>
            <FormSubtitle>Sign in to your account to continue</FormSubtitle>

            <Form onSubmit={handleSubmit}>
              <InputGroup>
                <StyledLabel htmlFor="email">Email Address</StyledLabel>
                <StyledInput
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                />
              </InputGroup>
            
              <InputGroup>
                <StyledLabel htmlFor="password">Password</StyledLabel>
                <StyledInput
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                />
              </InputGroup>
            
              <LoginButton type="submit" disabled={isLoading}>
                {isLoading ? 'Signing In...' : 'Sign In'}
              </LoginButton>

              {error && <ErrorMessage>{error}</ErrorMessage>}
            </Form>
          </FormContainer>
        </RightSection>
      </LoginCard>

      <DeveloperCredit>
        Developed by Sandfish
      </DeveloperCredit>
    </LoginContainer>
  );
};

export default Login; 